import { Room, RoomEvent, ConnectionState, AudioPresets } from "livekit-client";

export class LiveKitManager {
  constructor(trackHandler) {
    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      audioPresets: AudioPresets.speech,
    });

    this.trackHandler = trackHandler;
    this.onTranscriptMessage = null;
    this.onRPCMessage = null;

    this.setupEventListeners();
  }

  async connect(url, { token }) {
    if (this.room.state === ConnectionState.Connecting) {
      console.warn("Already connecting to a room, ignoring this request");
      return false;
    }

    if (this.isConnected()) {
      console.log("Disconnecting from current room before connecting...");
      await this.cleanup();
    }

    try {
      console.log(
        "Connecting to LiveKit with token:",
        token.substring(0, 20) + "...",
      );
      await this.room.connect(url, token);
      console.log("Connected to room:", this.room.name);

      return true;
    } catch (error) {
      console.error("Error connecting to room:", error);
      throw error;
    }
  }

  setupEventListeners() {
    this.room.on(RoomEvent.Disconnected, (reason) => {
      console.log("Disconnected:", reason);
    });

    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log("Connection state changed:", state);
    });

    this.room.on(
      RoomEvent.TrackSubscribed,
      (track, publication, participant) => {
        console.log(
          `Track subscribed: ${track.kind} from ${participant.identity}`,
        );

        if (this.trackHandler) {
          this.trackHandler({
            track,
            streams: track.mediaStream ? [track.mediaStream] : [],
            participant,
          });
        }
      },
    );

    this.room.on(
      RoomEvent.TrackUnsubscribed,
      (track, publication, participant) => {
        console.log(
          `Track unsubscribed: ${track.kind} from ${participant.identity}`,
        );
      },
    );

    this.room.registerTextStreamHandler(
      "ltranscript",
      async (reader, participantInfo) => {
        const text = await reader.readAll();
        if (this.onTranscriptMessage) {
          this.onTranscriptMessage(text);
        }
      },
    );

    this.room.registerRpcMethod("RPC_function_call", async (data) => {
      try {
        const payload = JSON.parse(data.payload);
        if (this.onRPCMessage) {
          return this.onRPCMessage(payload, data.participant, "rpc");
        }
      } catch (e) {
        console.error("Error handling RPC payload:", e);
        return JSON.stringify({ error: "Error processing RPC payload" });
      }
      return JSON.stringify({ status: "handler_not_set_or_ignored" });
    });

    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log("A new participant joined the room:", participant.identity);
    });
    this.room.on(RoomEvent.TrackPublished, (publication, participant) => {
  console.log(`The agent just announced a track: ${publication.kind}`);
});
  }

  async setMicrophoneEnabled(enabled) {
    if (this.room && this.room.localParticipant) {
      try {
        await this.room.localParticipant.setMicrophoneEnabled(enabled);
        return true;
      } catch (error) {
        console.error("Failed to toggle microphone:", error);
        return false;
      }
    }
    return false;
  }

  isConnected() {
    return this.room && this.room.state === ConnectionState.Connected;
  }

  async cleanup() {
    console.log("Cleaning up LiveKit Manager...");
    if (this.isConnected() || this.room.state === ConnectionState.Connecting) {
      try {
        await this.room.disconnect();
      } catch (error) {
        console.error("Error disconnecting room:", error);
      }
    }
  }

  setTranscriptHandler(callback) {
    this.onTranscriptMessage = callback;
  }

  setRPCHandler(callback) {
    this.onRPCMessage = callback;
  }
}
